import { useState } from 'react';
import { ChevronsUpDown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getInitials, getName } from '../../utils/userUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export interface ProfileMenuProps {
  username: string;
  email?: string;
  onLogout: () => void;
}

const ProfileMenu = ({ username, email, onLogout }: ProfileMenuProps) => {
  const { t } = useTranslation();
  const [loggingOut, setLoggingOut] = useState(false);

  if (!username) return null;

  // One logout per click: the item is disabled for the rest of this mount, which usually ends
  // right away since logging out swaps the whole tree for the login screen.
  const handleLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    onLogout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:hover:bg-zinc-800 dark:focus-visible:ring-zinc-100"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-medium text-white dark:bg-zinc-100 dark:text-zinc-900">
            {getInitials(username)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-50">
              {getName(username) || username}
            </span>
            <span className="truncate text-[11px] text-zinc-500 dark:text-zinc-400">{email || username}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-[232px] rounded-lg border-zinc-200 bg-white p-1 shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
      >
        <DropdownMenuLabel className="px-2 pb-2 pt-1.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-50">
              {getName(username) || username}
            </span>
            <span className="truncate text-xs font-normal text-zinc-500 dark:text-zinc-400">
              {email || username}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="-mx-1 my-1 bg-zinc-200 dark:bg-zinc-800" />
        <DropdownMenuItem
          disabled={loggingOut}
          onSelect={handleLogout}
          className="gap-2 rounded-md px-2 py-2 text-sm font-medium text-rose-600 focus:bg-rose-50 focus:text-rose-700 dark:text-rose-400 dark:focus:bg-rose-950 dark:focus:text-rose-300"
        >
          <LogOut className="h-4 w-4" />
          <span>{t('Log out')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileMenu;
